import { useEffect, useRef } from "react";

/**
 * Unsaved-changes protection for web forms — the browser-side mirror of the
 * desktop DirtyRegistry / DirtyState / UnsavedGuard trio.
 *
 * Two responsibilities:
 *   1. A module-level registry of open forms that currently hold unsaved user
 *      changes, so the session/heartbeat force-logout can warn before the work
 *      is discarded (see gracefulForceLogout in Services/tokenUtils.js).
 *   2. A hook, {@link useUnsavedGuard}, that a form calls with its live `dirty`
 *      boolean. It registers the form and installs a `beforeunload` prompt so
 *      closing the tab / refreshing / navigating away also warns.
 *
 * Modal-close (X / ESC / backdrop) is guarded separately by passing `dirty`
 * to ResponsiveModal, which shows an in-app "Leave / Stay" confirm.
 */

// Each entry: { getDirty: () => boolean, getTitle: () => string }
const forms = new Set();

/** True if ANY open registered form currently has unsaved changes. */
export function hasUnsavedChanges() {
  for (const f of forms) {
    try {
      if (f.getDirty()) return true;
    } catch {
      /* a broken form must never block logout */
    }
  }
  return false;
}

/**
 * Standard "leave without saving?" confirmation — the styled in-app dialog, so
 * every discard prompt matches ResponsiveModal's overlay. Returns a Promise:
 * `if (await confirmLeave()) onClose()` or `confirmLeave().then((ok) => ok && onClose())`.
 * (Re-exported from the shared component so all call sites keep importing it from here.)
 */
export { confirmLeave } from "../components/LeaveConfirmDialog";

/**
 * Track a form's unsaved-changes state.
 *  - registers it so the global logout / heartbeat guards can see it
 *  - installs a `beforeunload` prompt (tab close / refresh / navigate away)
 *
 * @param dirty  live boolean — true when the form has unsaved user changes
 * @param opts.title short human label used in warning messages
 */
export function useUnsavedGuard(dirty, { title = "this form" } = {}) {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const titleRef = useRef(title);
  titleRef.current = title;

  // Register once for the lifetime of the form; read the live value via refs.
  useEffect(() => {
    const entry = {
      getDirty: () => dirtyRef.current,
      getTitle: () => titleRef.current,
    };
    forms.add(entry);
    return () => {
      forms.delete(entry);
    };
  }, []);

  // Native browser prompt on tab close / refresh / hard navigation.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = ""; // Chrome requires returnValue to be set
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);
}
